/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';

import { usePanelActionsInternal } from '../../panel-manager/panel-provider';
import { usePanelStateInternal } from '../../panel-manager/use-panel-state-internal';
import { PanelContext } from '../../panels-renderer/panel-context';
import { PanelRouter } from '../../panels-renderer/panel-router';
import { PanelsRenderer } from '../../panels-renderer/panels-renderer';
import { PanelSlot } from './panel-slot';

const styles = cssMap({
	layout: {
		display: 'grid',
		gridTemplateAreas: `'main'`,
		gridTemplateColumns: 'minmax(0, 1fr)',
		gridTemplateRows: '1fr',
		height: '100%',
		width: '100%',
		// Panel is only shown as a separate column on large viewports
		'@media (min-width: 90rem)': {
			gridTemplateAreas: `
                "main panel"
           `,
			gridTemplateRows: 'auto',
			gridTemplateColumns: 'minmax(0,1fr) auto',
		},
		overflow: 'auto',
	},
	mainContent: {
		gridArea: 'main',
		overflow: 'auto',
		display: 'flex',
		height: '100%',
		isolation: 'isolate',
	},
	panelUplift: {
		overflow: 'hidden',
	},
});

export type LayoutWithPanelProps = {
	/**
	 * Panel width. Defaults to 365px.
	 * Can be overridden via resizing.
	 */
	panelWidth?: number;
	testId?: string;
	children: ReactNode;
	/**
	 * Called when resizing is complete.
	 * @param finalWidth number representing the new width of the panel in px
	 *
	 */
	onResizeEnd?: (finalWidth: number) => void;
};

export function LayoutWithPanel({
	children,
	panelWidth,
	testId,
	onResizeEnd,
}: LayoutWithPanelProps): JSX.Element {
	const manager = usePanelActionsInternal();
	const state = usePanelStateInternal();
	const isMotionUpliftEnabled = fg('platform-dst-motion-uplift-panel');

	const [persistedPanelWidth, setPersistedPanelWidth] = useState(panelWidth ?? state.panelWidth);
	const panelSlotTestId = testId && `${testId}--panel-slot`;

	const activePanelRef = useRef(state.activePanels[0]);
	const managerRef = useRef(manager);

	// Keep refs up to date on every render so the cleanup always sees the latest values
	activePanelRef.current = state.activePanels[0];
	managerRef.current = manager;

	useEffect(() => {
		/**
		 * Close the active panel when this component unmounts.
		 * Ensures panels do not persist when navigating to a new page.
		 */
		return () => {
			if (activePanelRef.current) {
				managerRef.current.closePanel(activePanelRef.current.instanceId);
			}
		};
	}, []);

	const handlePanelResizeEnd = (finalWidth: number) => {
		setPersistedPanelWidth(finalWidth);
		manager.setPanelWidth(finalWidth);
		onResizeEnd?.(finalWidth);
	};

	/**
	 * The layout position identifies the persistent panel slot, while the panel instance ID
	 * identifies the changing content within that slot. This keeps the slot mounted during a content
	 * replacement so only its content transitions.
	 */
	const renderedPanelSlots = state.activePanels.map((panel, index) => (
		<Fragment key={`panel-slot-${index}`}>
			<PanelSlot
				contentKey={panel.instanceId}
				defaultWidth={persistedPanelWidth}
				onResizeEnd={handlePanelResizeEnd}
				testId={panelSlotTestId}
			>
				<PanelContext.Provider value={panel}>
					<PanelRouter />
				</PanelContext.Provider>
			</PanelSlot>
		</Fragment>
	));

	return (
		<Box xcss={cx(styles.layout, isMotionUpliftEnabled && styles.panelUplift)} testId={testId}>
			<Box xcss={styles.mainContent}>{children}</Box>
			{isMotionUpliftEnabled ? (
				<ExitingPersistence>{renderedPanelSlots}</ExitingPersistence>
			) : (
				<PanelsRenderer>
					<PanelRouter
						wrapper={({ children }) => (
							<PanelSlot
								defaultWidth={persistedPanelWidth}
								onResizeEnd={handlePanelResizeEnd}
								testId={panelSlotTestId}
							>
								{children}
							</PanelSlot>
						)}
					/>
				</PanelsRenderer>
			)}
		</Box>
	);
}
